import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { apiUrl } from './api';

export type ChangeEvent = { t: 'ticket' | 'order' | 'session' | 'table' | 'menu' | 'print' | 'team'; s?: string; st?: string };

/** Quais telas cada tipo de evento invalida (os dados são buscados de novo pelas rotas normais, com permissões). */
const STAFF_KEYS: Record<ChangeEvent['t'], string[]> = {
  ticket: ['staff-station', 'staff-stations', 'staff-orders', 'staff-dashboard', 'staff-session', 'production-health'],
  order: ['staff-orders', 'staff-tables', 'staff-session', 'staff-dashboard'],
  session: ['staff-tables', 'staff-session', 'staff-dashboard', 'staff-account'],
  table: ['staff-tables', 'staff-dashboard'],
  print: ['staff-printing', 'staff-station', 'staff-stations', 'staff-dashboard', 'production-health'],
  team: ['staff-team'],
  menu: ['staff-menu', 'public-menu', 'staff-dashboard'],
};

/** Conexão SSE da equipe. O navegador reconecta sozinho se cair; as consultas periódicas continuam como rede de segurança. */
export function useStaffRealtime() {
  const qc = useQueryClient();
  const [live, setLive] = useState(false);
  useEffect(() => {
    const es = new EventSource(apiUrl('/api/staff/events'));
    es.addEventListener('ready', () => setLive(true));
    es.onerror = () => setLive(false);
    es.addEventListener('change', (ev) => {
      const e = JSON.parse((ev as MessageEvent).data) as ChangeEvent;
      for (const k of STAFF_KEYS[e.t] ?? []) void qc.invalidateQueries({ queryKey: [k] });
      window.dispatchEvent(new CustomEvent<ChangeEvent>('garlic:change', { detail: e }));
    });
    return () => es.close();
  }, [qc]);
  return live;
}

/** Tempo real do cliente na mesa: status dos pedidos da comanda e mudanças no cardápio. */
export function useGuestRealtime(enabled: boolean) {
  const qc = useQueryClient();
  useEffect(() => {
    if (!enabled) return;
    const es = new EventSource(apiUrl('/api/public/events'));
    es.addEventListener('change', (ev) => {
      const e = JSON.parse((ev as MessageEvent).data) as ChangeEvent;
      if (e.t === 'menu') void qc.invalidateQueries({ queryKey: ['public-menu'] });
      else {
        void qc.invalidateQueries({ queryKey: ['guest-session'] });
        void qc.invalidateQueries({ queryKey: ['guest-account'] });
        void qc.invalidateQueries({ queryKey: ['guest-receipt'] });
      }
    });
    return () => es.close();
  }, [qc, enabled]);
}

/** Relógio que atualiza a cada `ms` (cronômetros das comandas). */
export function useNow(ms = 1000) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(t);
  }, [ms]);
  return now;
}

/** Bipe curto (sem arquivo de áudio). Navegadores só tocam som depois de um clique do usuário na página. */
export function beep() {
  try {
    const ctx = new AudioContext();
    const play = (freq: number, at: number) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.frequency.value = freq;
      o.connect(g);
      g.connect(ctx.destination);
      g.gain.setValueAtTime(0.25, ctx.currentTime + at);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + at + 0.25);
      o.start(ctx.currentTime + at);
      o.stop(ctx.currentTime + at + 0.25);
    };
    play(880, 0);
    play(1175, 0.18);
    setTimeout(() => void ctx.close(), 800);
  } catch {
    /* sem áudio disponível */
  }
}
